import { makeAutoObservable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import { IMAGES_BY_COMPANY } from '~/constants/images';

import { storage as localStorage } from '../stores/LocalStorage';

import { CeriseConfig } from './configs/Carrefour';
import { CircleKConfig } from './configs/CircleK';
import { CityDrinksConfig } from './configs/CityDrinks';
import { HeimkaupConfig } from './configs/Heimkaup';
import { JiffyConfig } from './configs/Jiffy';
import { KidsConfig } from './configs/Kids';
import { LocaleeConfig } from './configs/Localee';
import { OkieConfig } from './configs/Okie';
import { OskuhusConfig } from './configs/Oskuhus';
import { ViloConfig } from './configs/Vilo';
import { CompanyConfig, CompanyName, EnvVariables } from './interface';

export const DEFAULT_COMPANIES = [
  CompanyName.Localee,
  CompanyName.CircleK,
  CompanyName.Vilo,
  CompanyName.Jiffy,
  CompanyName.Kids,
  CompanyName.Okie,
  CompanyName.Oskuhus,
  CompanyName.Heimkaup,
  CompanyName.Cerise,
];

class Company {
  private currentCompany: CompanyName = CompanyName.CityDrinks;
  private isOriginalCompany: boolean = true;
  private variables: EnvVariables = {};
  private readonly configs: Record<CompanyName, CompanyConfig> = {
    [CompanyName.CityDrinks]: CityDrinksConfig,
    [CompanyName.Localee]: LocaleeConfig,
    [CompanyName.CircleK]: CircleKConfig,
    [CompanyName.Vilo]: ViloConfig,
    [CompanyName.Jiffy]: JiffyConfig,
    [CompanyName.Kids]: KidsConfig,
    [CompanyName.Okie]: OkieConfig,
    [CompanyName.Oskuhus]: OskuhusConfig,
    [CompanyName.Heimkaup]: HeimkaupConfig,
    [CompanyName.Cerise]: CeriseConfig,
  };

  isActiveRecaptchaDevMode = false;

  constructor() {
    makeAutoObservable(this);

    makePersistable(this, {
      name: 'Company',
      properties: ['isActiveRecaptchaDevMode'],
      storage: localStorage,
    }).catch((error) => error && console.error(error));

    if (import.meta.env.SSR) {
      return;
    }

    const contextData = window.__INITIAL_STATE__.context;

    if (import.meta.env.REACT_APP_ENV !== 'production') {
      this.setIsOriginalCompanyLogic(!!contextData.isOriginalLogic);
    }

    this.setCompanyName(contextData.company);
    this.setVariables(contextData.variables as EnvVariables);

    const rootDiv = document && document?.getElementById('app');
    rootDiv?.setAttribute('data-company', `${contextData.company}`);
  }

  setCompanyName(name: CompanyName) {
    this.currentCompany = name;
  }

  setVariables(variables: EnvVariables) {
    this.variables = variables;
  }

  setIsOriginalCompanyLogic(yes: boolean) {
    this.isOriginalCompany = yes;
  }

  setIsActiveRecaptchaDevMode(active: boolean) {
    this.isActiveRecaptchaDevMode = active;
  }

  get config(): CompanyConfig {
    if (this.isOriginalCompany) {
      return this.configs[this.currentCompany];
    } else {
      const company = { ...this.configs[this.currentCompany] };
      //**Use default company id LOCALEE if we check only UI and no other logic! */
      company.id = 'd05f30ba-ceb5-4633-a3b6-aa0f9463954f';

      company.warehouse = {
        default: 'NJSB1',
        minimalOrderFeeWarehouseCodes: [],
        location: { lat: 40.8537291, lng: -74.0197964 },
      };

      company.map = {
        center: { lat: 40.8537291, lng: -74.0197964 },
        zoom: 12,
      };

      return company;
    }
  }

  get name(): CompanyName {
    return this.currentCompany;
  }

  get envVariables(): EnvVariables {
    return this.variables;
  }

  get images() {
    return IMAGES_BY_COMPANY[this.currentCompany];
  }

  get currency() {
    return this.config.currency;
  }

  get isHidePaymentMethodsPage(): boolean {
    return [''].includes(this.currentCompany);
  }

  get isDisableFreshChat(): boolean {
    return [''].includes(this.currentCompany);
  }

  get isHideFavourites(): boolean {
    return [''].includes(this.currentCompany);
  }

  get isDisableLoyaltyProgram(): boolean {
    return [''].includes(this.currentCompany);
  }

  get isHideMapLegend(): boolean {
    return ![''].includes(this.currentCompany);
  }

  get isShowTaxes(): boolean {
    return [''].includes(this.currentCompany);
  }

  get isExceptionAlcohol(): boolean {
    return ![''].includes(this.currentCompany);
  }

  get isHideTracking(): boolean {
    return [''].includes(this.currentCompany);
  }

  get hasAddress2(): boolean {
    return [''].includes(this.currentCompany);
  }

  get isTrimTrailingZeros(): boolean {
    return ![''].includes(this.currentCompany);
  }

  get isShowRateExperience(): boolean {
    return ![''].includes(this.currentCompany);
  }

  get hasWelcomeBlock(): boolean {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get isAvailableCartInMobileHeader(): boolean {
    return ![...DEFAULT_COMPANIES].includes(this.currentCompany);
  }

  get showETAInfoMobileHeader(): boolean {
    return [...DEFAULT_COMPANIES].includes(this.currentCompany);
  }

  get showPersonalDataButtonMobileCabinetPage() {
    return [...DEFAULT_COMPANIES].includes(this.currentCompany);
  }

  get showLocationIconMobileCabinetPage() {
    return DEFAULT_COMPANIES.includes(this.currentCompany);
  }

  get showSectionsMobileProductPage() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get showSearchHeaderProductPage(): boolean {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get showShowShareButtonsProductPage(): boolean {
    return [...DEFAULT_COMPANIES].includes(this.currentCompany);
  }

  get showActionButtonsMobileProductPage() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get showShortReviewListMobileProductPage() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get showDescriptionReviewProductPage() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get customMobileRatingProductPage() {
    return [...DEFAULT_COMPANIES].includes(this.currentCompany);
  }

  get showExpressBadgeInSliderProductPage() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get isVerticalSwiperPageDesktop() {
    return [...DEFAULT_COMPANIES].includes(this.currentCompany);
  }

  get hideAboutUs() {
    return [...DEFAULT_COMPANIES].includes(this.currentCompany);
  }

  get nonCloseableNavMobileShopPage() {
    return DEFAULT_COMPANIES.includes(this.currentCompany);
  }

  get showCardDisclaimer() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get promocodeInInputResetCheckout() {
    return [
      CompanyName.Localee,
      CompanyName.Oskuhus,
      CompanyName.CityDrinks,
    ].includes(this.currentCompany);
  }

  get loyaltyProgramEnabled() {
    return [
      CompanyName.Jiffy,
      CompanyName.Localee,
      CompanyName.Cerise,
      CompanyName.Heimkaup,
    ].includes(this.currentCompany);
  }

  get buttonOutPanelCheckout() {
    return DEFAULT_COMPANIES.includes(this.currentCompany);
  }

  get postfixPrice() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get needVerification() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }
  get showButtonInTitleAddress() {
    return [...DEFAULT_COMPANIES].includes(this.currentCompany);
  }

  get showCartMobileSubtotal() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get showMobileDowloadPanel() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get showExpressDeliveryBannerMainPage() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get useAwsPaymentMethod() {
    return this.isIncludedIn([CompanyName.CircleK]);
  }

  get isSommelierEnabled() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isDeliveryInfoModalEnabled() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showCompanyLogoOnMainMobile() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get showAdditionalTextEmptyPayments() {
    return [CompanyName.Localee].includes(this.currentCompany);
  }

  get deliveryMethodSelectionAvailable() {
    return DEFAULT_COMPANIES.includes(this.currentCompany);
  }

  get isShareBlockCaptionVisible() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isShareBlockButtonIconVisible() {
    return this.isIncludedIn([CompanyName.Localee]);
  }

  get needKycCheck() {
    return this.match({
      citydrinks: true,
      localee: false,
      vilo: false,
      jiffy: false,
      ourkids: false,
      okie: false,
      oskuhus: false,
      heimkaup: false,
      default: false,
    });
  }

  get hasOnboarding() {
    return ![CompanyName.Cerise].includes(this.currentCompany);
  }

  get hasOnboardingAutoplay() {
    return ![CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get hasOnboardingCloseButton() {
    return ![CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get hasOnboardingDotsInHeader() {
    return ![CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get hasNewOnboardingDesign() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get hasOnboardingDotsAboveButtons() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get showStartShopping() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get showCartMobileFavsPage() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get showCartModalCount() {
    return this.isIncludedIn([CompanyName.Localee, CompanyName.CircleK]);
  }

  get showCartAdditionalTextMobile() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get missAgeRestrictionModal() {
    return this.isIncludedIn([
      CompanyName.CircleK,
      CompanyName.Vilo,
      CompanyName.Kids,
      CompanyName.Okie,
      CompanyName.Oskuhus,
      CompanyName.Cerise,
    ]);
  }

  get hideReviews() {
    return [
      CompanyName.CircleK,
      CompanyName.Cerise,
      CompanyName.Okie,
      CompanyName.CityDrinks,
    ].includes(this.currentCompany);
  }

  get hideFreeDeliveryProgressBar() {
    return false; // TODO: uncomment - this.currentCompany === CompanyName.CityDrinks;
  }

  get showCartCoutToRemove() {
    return this.currentCompany === CompanyName.CityDrinks;
  }

  get addressModalDefault() {
    return DEFAULT_COMPANIES.includes(this.currentCompany);
  }

  get combineItemsByDefaut() {
    return [CompanyName.CityDrinks].includes(this.currentCompany);
  }

  get isDefaultCompany() {
    return [...DEFAULT_COMPANIES].includes(this.currentCompany);
  }

  get showPartiallyPickedInfo() {
    return ![CompanyName.Vilo].includes(this.currentCompany);
  }

  get hideFreezeMoneyMessage() {
    return this.isIncludedIn([CompanyName.Vilo]);
  }

  get hideDownloadAppPanel() {
    return this.isIncludedIn([CompanyName.CircleK, CompanyName.Kids]);
  }

  get isEnabledRecaptcha() {
    if (import.meta.env.REACT_APP_ENV !== 'production') {
      return this.isActiveRecaptchaDevMode;
    }
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isReviewPanelHidden() {
    return this.currentCompany === CompanyName.CircleK;
  }

  get showLinkedinIcon() {
    return this.isIncludedIn([CompanyName.CircleK]);
  }

  get isEnabledIndexNow() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isFavsInCabinet() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showMatchingProducts_ProductPage() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showMatchingProducts_CartPage() {
    return this.isIncludedIn([CompanyName.CityDrinks, ...DEFAULT_COMPANIES]);
  }

  get showSimilarProducts_ProductPage() {
    return this.isIncludedIn([CompanyName.CityDrinks, ...DEFAULT_COMPANIES]);
  }
  get isDefaultHeaderInCatalog() {
    return !this.isIncludedIn([CompanyName.CityDrinks]);
  }
  get isEnabledIconOnAddToCartButton() {
    return this.isIncludedIn([CompanyName.Cerise]);
  }

  isCompanyNameExist(name: string): boolean {
    return this.configs[name as CompanyName] !== undefined;
  }

  isIncludedIn(nameList: string[]): boolean {
    return nameList.includes(this.currentCompany);
  }

  // Return type: T if passed all companies or default, else T | undefined
  match<T>(record: { default?: T } & Record<CompanyName, T>): T;
  match<T>(record: { default: T } & Partial<Record<CompanyName, T>>): T;
  match<T>(record: Partial<Record<CompanyName | 'default', T>>): T | undefined;
  match<T>(
    record: { default?: T } & Partial<Record<CompanyName, T>>,
  ): T | undefined {
    return this.currentCompany in record
      ? record[this.currentCompany]
      : record.default;
  }

  // Return type: T if passed all design variants or default, else T | undefined
  variant<T>(record: { default?: T } & Record<'1' | '2', T>): T;
  variant<T>(record: { default: T } & Partial<Record<'1' | '2', T>>): T;
  variant<T>(record: Partial<Record<'1' | '2' | 'default', T>>): T | undefined;
  variant<T>(
    record: { default?: T } & Partial<Record<'1' | '2', T>>,
  ): T | undefined {
    return this.currentCompany === CompanyName.CityDrinks
      ? record['1']
      : record['2'];
  }
}

export const company = new Company();
